// QuestionFormPage — a website "ask us" form whose answers land in the Engine
// Inbox (Inbox > Questions) and are copied to support@ (mct_7427fc9e-e3f).
//
//   /questions/:kind            — standalone page in the TSB shell
//   /questions/:kind?embed=1    — the form alone, for an iframe on another
//                                 site (the billing page). It reports its own
//                                 height to the parent page so the frame grows
//                                 with it; see seeds/sops/sop-ee-website-questions.md.
//
// The fields and wording come from the engine (GET /api/public/inquiries/:kind)
// so one page serves every kind: billing (the billing page) and contact
// (the TSB /contact page). A field with options is drawn as a dropdown.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Field { key: string; label: string; help?: string; placeholder?: string; required: boolean; max: number; options?: Array<{ value: string; label: string }> }
interface Form {
  kind: string; label: string; heading: string; intro: string; messageLabel: string; messagePlaceholder: string;
  emailHelp?: string; submitLabel: string; thanksHeading: string; thanksBody: string; fields: Field[];
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.15, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 24px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)' };
const label: CSSProperties = { display: 'block', fontWeight: 600, fontSize: 15, color: '#0A0A2E', marginBottom: 6 };
const help: CSSProperties = { fontSize: 13, color: '#6b6b80', margin: '-2px 0 6px' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', fontFamily: 'inherit' };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const row: CSSProperties = { marginBottom: 16 };

export default function QuestionFormPage() {
  const { kind = '' } = useParams();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  const [form, setForm] = useState<Form | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<{ heading: string; body: string } | null>(null);
  const started = useRef(Date.now());
  const hp = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API}/api/public/inquiries/${encodeURIComponent(kind)}`)
      .then(async (r) => {
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        setForm((await r.json()).form as Form);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [kind]);

  // Tell the parent page how tall we are, so its iframe can fit the form.
  useEffect(() => {
    if (!embed || !root.current || typeof ResizeObserver === 'undefined') return;
    const post = () => window.parent?.postMessage({ type: 'tsb-question-form-height', kind, height: Math.ceil(root.current!.getBoundingClientRect().height) + 4 }, '*');
    const ro = new ResizeObserver(post);
    ro.observe(root.current);
    post();
    return () => ro.disconnect();
  }, [embed, kind, state, done]);

  const set = (k: string, v: string) => setValues((cur) => ({ ...cur, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setBusy(true); setErr('');
    try {
      const fields: Record<string, string> = {};
      for (const f of form.fields) fields[f.key] = values[f.key] ?? '';
      const pageUrl = embed ? (document.referrer || '') : window.location.href;
      const r = await fetch(`${API}/api/public/inquiries/${encodeURIComponent(kind)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: values.firstName ?? '', lastName: values.lastName ?? '', email: values.email ?? '',
          message: values.message ?? '', fields, pageUrl, hp: hp.current?.value ?? '', formMs: Date.now() - started.current,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}). Please try again.`); return; }
      setDone({ heading: d.thanksHeading ?? form.thanksHeading, body: d.thanksBody ?? form.thanksBody });
      if (!embed) window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  let body: React.ReactNode;
  if (state === 'loading') body = <p style={lead}>Loading…</p>;
  else if (state === 'missing') body = <p style={lead}>We could not find that form.</p>;
  else if (state === 'error' || !form) body = <p style={lead}>Something went wrong loading this form. Please refresh the page, or email support@thesuccessfulbookkeeper.com.</p>;
  else if (done) {
    body = (
      <div style={card} role="status">
        <p style={{ ...lead, margin: '0 0 8px', color: '#0A0A2E' }}><strong>{done.heading}</strong></p>
        <p style={{ ...lead, margin: 0 }}>{done.body}</p>
      </div>
    );
  } else {
    body = (
      <>
        {!embed && <><h1 style={h1}>{form.heading}</h1><p style={lead}>{form.intro}</p></>}
        <form onSubmit={submit} style={embed ? undefined : card} noValidate={false}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 14px' }}>
            <div style={row}>
              <label htmlFor="q-first" style={label}>First name *</label>
              <input id="q-first" required autoComplete="given-name" maxLength={80} style={input} value={values.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} />
            </div>
            <div style={row}>
              <label htmlFor="q-last" style={label}>Last name *</label>
              <input id="q-last" required autoComplete="family-name" maxLength={80} style={input} value={values.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} />
            </div>
          </div>
          <div style={row}>
            <label htmlFor="q-email" style={label}>Email *</label>
            {form.emailHelp && <p style={help}>{form.emailHelp}</p>}
            <input id="q-email" type="email" required autoComplete="email" style={input} value={values.email ?? ''} onChange={(e) => set('email', e.target.value)} />
          </div>
          {form.fields.map((f) => (
            <div key={f.key} style={row}>
              <label htmlFor={`q-${f.key}`} style={label}>{f.label}{f.required ? ' *' : ''}</label>
              {f.help && <p style={help}>{f.help}</p>}
              {f.options ? (
                <select id={`q-${f.key}`} required={f.required} style={input}
                  value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}>
                  <option value="" disabled>Please select</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input id={`q-${f.key}`} required={f.required} maxLength={f.max} placeholder={f.placeholder} style={input}
                  value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} />
              )}
            </div>
          ))}
          <div style={row}>
            <label htmlFor="q-message" style={label}>{form.messageLabel} *</label>
            <textarea id="q-message" required rows={5} maxLength={5000} placeholder={form.messagePlaceholder} style={{ ...input, resize: 'vertical' }}
              value={values.message ?? ''} onChange={(e) => set('message', e.target.value)} />
          </div>
          {/* Honeypot: hidden from people, filled by bots. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="q-website">Website</label>
            <input id="q-website" ref={hp} tabIndex={-1} autoComplete="off" name="website" />
          </div>
          <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : form.submitLabel}</button>
          {err && <p role="alert" style={{ color: '#B42318', fontSize: 15, margin: '12px 0 0' }}>{err}</p>}
        </form>
      </>
    );
  }

  if (embed) {
    return (
      <div ref={root} style={{ fontFamily: "'Inter', 'Poppins', Arial, sans-serif", padding: '4px 2px', background: 'transparent' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap'); body{margin:0;background:transparent}`}</style>
        {body}
      </div>
    );
  }
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div ref={root} style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>{body}</div>
    </TakerShell>
  );
}
